/**
 * 레이지 무브 독 — 화면 좌측 중앙의 세로 아이콘 2개(💪 쓸어버리기 / ┻━┻ 책상 뒤집기).
 * 아이콘 클릭 = 제스처 모드 토글(RageGestures) — 실제 발동은 캔버스 위 드래그 제스처.
 * 사용 제한(UI가 소유):
 *  - 쓸어버리기: 발동(rage:sweep) 후 실시간 쿨다운 — 아이콘 위 오버레이가 줄어드는 것으로 표시.
 *  - 책상 뒤집기: desk:destroyed(뒤집기든 내리치기 반파든)가 발행되면 세션 리셋까지 비활성.
 * 모드가 켜지면 하단에 조작 힌트를 띄운다.
 */
import type { EventBus } from '../core/EventBus';
import type { RageGestures, RageMode } from '../input/RageGestures';
import { BALANCE } from '../config/balance';
import { t } from '../i18n/i18n';

export class RageDock {
  private rootEl: HTMLElement;
  private sweepBtn: HTMLButtonElement;
  private flipBtn: HTMLButtonElement;
  private sweepCd: HTMLElement;
  private hintEl: HTMLElement;
  private cdTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    bus: EventBus,
    private gestures: RageGestures,
  ) {
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="rage-dock hidden" id="dr-rage">
        <button class="dock-btn" id="dr-sweep" title="${t('sweepBtn')}">
          <span class="ico">💪</span><span class="lbl">${t('sweepBtn')}</span>
          <span class="cd"></span>
        </button>
        <button class="dock-btn" id="dr-flip" title="${t('flipBtn')}">
          <span class="ico">┻━┻</span><span class="lbl">${t('flipBtn')}</span>
        </button>
      </div>
      <div class="rage-hint hidden" id="dr-rage-hint"></div>`;
    parent.appendChild(wrap);

    this.rootEl = wrap.querySelector('#dr-rage')!;
    this.sweepBtn = wrap.querySelector<HTMLButtonElement>('#dr-sweep')!;
    this.flipBtn = wrap.querySelector<HTMLButtonElement>('#dr-flip')!;
    this.sweepCd = this.sweepBtn.querySelector<HTMLElement>('.cd')!;
    this.hintEl = wrap.querySelector('#dr-rage-hint')!;

    this.sweepBtn.addEventListener('click', () => this.toggle('sweep'));
    this.flipBtn.addEventListener('click', () => this.toggle('flip'));
    this.gestures.onModeChange = (m) => this.renderMode(m);

    // 스윕 발동 → 쿨다운 시작(모드 해제는 RageGestures가 이미 함).
    bus.on('rage:sweep', () => this.startCooldown());
    // 책상이 사라지면(뒤집기/내리치기 반파 공통) 뒤집기 비활성.
    bus.on('desk:destroyed', () => (this.flipBtn.disabled = true));
    bus.on('session:start', () => {
      this.resetButtons();
      this.setVisible(true);
    });
    bus.on('session:reset', () => this.resetButtons());
    bus.on('session:end', () => this.setVisible(false));
  }

  private toggle(m: Exclude<RageMode, null>): void {
    const btn = m === 'sweep' ? this.sweepBtn : this.flipBtn;
    if (btn.disabled) return;
    this.gestures.setMode(this.gestures.mode === m ? null : m);
  }

  private renderMode(m: RageMode): void {
    this.sweepBtn.classList.toggle('active', m === 'sweep');
    this.flipBtn.classList.toggle('active', m === 'flip');
    this.hintEl.classList.toggle('hidden', m === null);
    if (m) this.hintEl.textContent = t(m === 'sweep' ? 'sweepHint' : 'flipHint');
  }

  private startCooldown(): void {
    const sec = BALANCE.rage.sweepCooldownSec;
    this.sweepBtn.disabled = true;
    // 오버레이를 즉시 가득 채운 뒤(트랜지션 없이) 쿨다운 시간 동안 선형으로 비운다.
    this.sweepCd.style.transition = 'none';
    this.sweepCd.style.transform = 'scaleX(1)';
    void this.sweepCd.offsetWidth; // 리플로우로 트랜지션 리셋 확정
    this.sweepCd.style.transition = `transform ${sec}s linear`;
    this.sweepCd.style.transform = 'scaleX(0)';
    this.cdTimer = window.setTimeout(() => {
      this.sweepBtn.disabled = false;
      this.cdTimer = null;
    }, sec * 1000);
  }

  private resetButtons(): void {
    if (this.cdTimer !== null) {
      clearTimeout(this.cdTimer);
      this.cdTimer = null;
    }
    this.sweepBtn.disabled = false;
    this.sweepCd.style.transition = 'none';
    this.sweepCd.style.transform = 'scaleX(0)';
    this.flipBtn.disabled = false;
    this.renderMode(this.gestures.mode);
  }

  setVisible(v: boolean): void {
    this.rootEl.classList.toggle('hidden', !v);
    if (!v) this.hintEl.classList.add('hidden');
  }
}
